import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Button, Alert, Badge, Spinner } from 'react-bootstrap';
import LookupLayout from '../layout/LookupLayout';
import { bulkChangeOwnerRecords } from '../../api/BulkOwnerTransferApi';

/**
 * BulkOwnerTransferModal
 */
export default function BulkOwnerTransferModal({
  show = false,
  onHide = () => {},
  onClose = () => {},
  objectName,
  selectedIds = [],
}) {
  const totalSelected = Array.isArray(selectedIds) ? selectedIds.length : 0;

  const [newOwner, setNewOwner] = useState(null); // { id, label }
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const [result, setResult] = useState(null); // { total, updated, failed, errors: [] }

  const canSubmit = useMemo(() => {
    return (
      !!objectName &&
      totalSelected > 0 &&
      !!newOwner?.id &&
      !submitting
    );
  }, [objectName, totalSelected, newOwner, submitting]);

  const resetState = useCallback(() => {
    setNewOwner(null);
    setSubmitting(false);
    setApiError('');
    setResult(null);
  }, []);

  useEffect(() => {
    if (show) {
      resetState();
    }
  }, [show, resetState]);

  const handleClose = () => {
    const didComplete = !!result;
    onClose({ didComplete, result });
    onHide();
  };

  const handleConfirm = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setApiError('');
    setResult(null);

    try {
      const response = await bulkChangeOwnerRecords(
        objectName,
        selectedIds,
        newOwner.id,
        { batchSize: 50, continueOnError: true }
      );
      const data = response?.data?.data || response?.data;
      setResult(data);
    } catch (e) {
      const d = e?.response?.data;
      const msg =
        (typeof d?.message === 'string' && d.message) ||
        (typeof d?.error === 'string' && d.error) ||
        e?.message ||
        'Failed to change owner. Please try again.';
      setApiError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Bulk Owner Transfer</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {apiError && (
          <Alert variant="danger">{apiError}</Alert>
        )}

        {result ? (
          <div>
            <div className="mb-3">
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <Badge bg="secondary">Total: {result.total ?? totalSelected}</Badge>
                <Badge bg="success">Updated: {result.updated ?? 0}</Badge>
                <Badge bg="danger">Failed: {result.failed ?? 0}</Badge>
              </div>
            </div>

            {Array.isArray(result.errors) && result.errors.length > 0 ? (
              <Alert variant="warning" className="mb-0">
                <div className="fw-semibold mb-2">Errors (showing up to 10)</div>
                <ul className="mb-0">
                  {result.errors.slice(0, 10).map((er, idx) => (
                    <li key={`${er?.id || idx}-${idx}`}>
                      <code>{er?.id}</code> — {er?.error || 'Failed'}
                    </li>
                  ))}
                  {result.errors.length > 10 && (
                    <li className="text-muted">
                      ... and {result.errors.length - 10} more
                    </li>
                  )}
                </ul>
              </Alert>
            ) : (
              <Alert variant="success" className="mb-0">
                Owner transfer completed successfully.
              </Alert>
            )}
          </div>
        ) : (
          <div>
            <div className="mb-2 text-muted">
              Selected records: <strong>{totalSelected}</strong>
            </div>

            <div className="mb-3">
              <label className="form-label mb-1">New Owner</label>
              <LookupLayout
                objectName="user"
                searchFields="firstname,lastname,email,username"
                displayFields="firstname,lastname,email"
                value={newOwner?.id || ''}
                defaultLabel={newOwner?.label || ''}
                onChange={(id, label) => setNewOwner(id ? { id, label } : null)}
                placeholder="Search user by name or email…"
                disabled={submitting}
                dropdownZIndex={10600}
                activeUsersOnly
              />
            </div>

            <Alert variant="info" className="mb-0">
              This will update <strong>owner</strong> for the selected records.
            </Alert>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {result ? (
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={!canSubmit}
            >
              {submitting ? (
                <>
                  <Spinner
                    animation="border"
                    size="sm"
                    role="status"
                    className="me-2"
                  />
                  Transferring...
                </>
              ) : (
                'Confirm Transfer'
              )}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}

